import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SiInstagram, SiWhatsapp } from 'react-icons/si';
import { FaPhone, FaEnvelope } from 'react-icons/fa';
import BookingModal from './BookingModal';
import './landing-nxtlvl.css';

/* ============================================================================
   FEATURED EVENT LOGIC
   ----------------------------------------------------------------------------
   Auto-rotates between Shakai's Friday + Saturday nights based on today's date.
   - Fri → DOA Fridays (tonight)
   - Sat → Prime Saturdays (tonight)
   - Other days → upcoming Friday's DOA

   To feature a SPECIAL event (guest DJ, takeover, holiday, etc.) just fill in
   OVERRIDE_EVENT below. Leave it null when not in use — auto-rotation resumes.

   To hide the event card entirely, set OVERRIDE_EVENT = { hidden: true }.
   ============================================================================ */

// Manual override — fill in when you want to feature a special event.
// Leave as null to use the default Friday/Saturday rotation.
const OVERRIDE_EVENT = null;
// Example:
// const OVERRIDE_EVENT = {
//   date: '2026-05-02',
//   dateLabel: 'Sat · May 2',
//   title: 'Special Guest Night ⛩',
//   artist: 'DJ Name',
//   venue: '43 E Pine St · Downtown Orlando',
//   description: 'Doors at 10. Special set, special crowd.',
// };

// Shakai's two weekly nights — edit these once and they rotate forever
const SHAKAI_VENUE = '43 E Pine St · Downtown Orlando';

const DOA_FRIDAY_TEMPLATE = {
  title: 'DOA Fridays at Shakai ⛩',
  artist: 'Resident DJs',
  venue: SHAKAI_VENUE,
  description: 'Free before 11. Free drinks for ladies before 12.'
};

const PRIME_SATURDAY_TEMPLATE = {
  title: 'Prime Saturdays at Shakai ⛩',
  artist: 'Resident DJs',
  venue: SHAKAI_VENUE,
  description: 'Free before 11. Free drinks for ladies before 12.',
};

// Build the featured event for today
function getFeaturedEvent() {
  if (OVERRIDE_EVENT) {
    return OVERRIDE_EVENT.hidden ? null : OVERRIDE_EVENT;
  }

  const today = new Date();
  const day = today.getDay(); // 0=Sun, 5=Fri, 6=Sat

  // Decide which template + which date
  let template;
  let eventDate;
  let isTonight;

  if (day === 5) {
    template = DOA_FRIDAY_TEMPLATE;
    eventDate = today;
    isTonight = true;
  } else if (day === 6) {
    template = PRIME_SATURDAY_TEMPLATE;
    eventDate = today;
    isTonight = true;
  } else {
    // Any other day → upcoming Friday
    template = DOA_FRIDAY_TEMPLATE;
    eventDate = new Date(today);
    const daysUntilFriday = (5 - day + 7) % 7 || 7; // never 0 here
    eventDate.setDate(today.getDate() + daysUntilFriday);
    isTonight = false;
  }

  // Format date strings
  const yyyy = eventDate.getFullYear();
  const mm = String(eventDate.getMonth() + 1).padStart(2, '0');
  const dd = String(eventDate.getDate()).padStart(2, '0');
  const isoDate = `${yyyy}-${mm}-${dd}`;

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const dayLabel = dayNames[eventDate.getDay()];
  const monthLabel = monthNames[eventDate.getMonth()];
  const dateLabel = isTonight
    ? `Tonight · ${dayLabel} ${monthLabel} ${eventDate.getDate()}`
    : `${dayLabel} · ${monthLabel} ${eventDate.getDate()}`;

  return {
    ...template,
    date: isoDate,
    dateLabel,
  };
}

const FEATURED_EVENT = getFeaturedEvent();

/* ========================================================================== */

function Landing({ onEnter }) {
  // modalState: null | { type, prefillDate?, eventName? }
  const [modalState, setModalState] = useState(null);

  const openVIP = (eventContext) =>
    setModalState({ type: 'vip', ...eventContext });
  const openGuestList = (eventContext) =>
    setModalState({ type: 'guestlist', ...eventContext });

  return (
    <div className="nxt">
      {/* Atmosphere */}
      <div className="nxt-aura" aria-hidden="true" />
      <div className="nxt-grid" aria-hidden="true" />
      <div className="nxt-grain" aria-hidden="true" />

      {/* Top bar */}
      <header className="nxt-top">
        <div className="nxt-mark">
          <span className="nxt-mark-dot" />
          <span className="nxt-mark-text">NXTLVL</span>
        </div>
        <div className="nxt-meta">
          <span className="nxt-meta-loc">ORLANDO · FL</span>
          {FEATURED_EVENT && (
            <span className="nxt-meta-status">
              <span className="nxt-pulse" />
              NEXT EVENT · {FEATURED_EVENT.dateLabel.toUpperCase()}
            </span>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="nxt-hero">
        <p className="nxt-eyebrow">
          <span className="nxt-eyebrow-line" />
          <span>The Network</span>
        </p>

        <h1 className="nxt-wordmark">
          NXT<span className="nxt-wordmark-accent">LVL</span>
        </h1>

        <p className="nxt-tagline">
          Orlando&rsquo;s nightlife network. Tables, lists, and the brand
          behind the night.
        </p>

        <div className="nxt-cta-row">
          <button
            className="nxt-btn nxt-btn--primary"
            onClick={() => openVIP()}
          >
            <span className="nxt-btn-text">Reserve a Table</span>
            <span className="nxt-btn-arrow" aria-hidden="true">→</span>
          </button>
          <button
            className="nxt-btn nxt-btn--ghost"
            onClick={() => openGuestList()}
          >
            <span className="nxt-btn-text">Free Guest List</span>
          </button>
        </div>
      </section>

      {/* Ventures grids */}
      <section className="nxt-ventures">
        <p className="nxt-section-label">
          <span>The Universe</span>
          <span className="nxt-section-count">04 / VENTURES</span>
        </p> 

        <div className="nxt-venture-grid">
          {/* 01 — This Weekend */}
          {FEATURED_EVENT ? (
            <button
              className="nxt-event"
              onClick={() =>
                openVIP({
                  prefillDate: FEATURED_EVENT.date,
                  eventName: FEATURED_EVENT.title,
                })
              }
            >
              <div className="nxt-event-head">
                <span className="nxt-event-num">01 / THIS WEEKEND</span>
                <span className="nxt-event-date">
                  {FEATURED_EVENT.dateLabel}
                </span>
              </div>

              <div className="nxt-event-body">
                <h3 className="nxt-event-title">{FEATURED_EVENT.title}</h3>
                <p className="nxt-event-meta">
                  <span>{FEATURED_EVENT.artist}</span>
                  <span className="nxt-event-meta-sep">·</span>
                  <span>{FEATURED_EVENT.venue}</span>
                </p>
                {FEATURED_EVENT.description && (
                  <p className="nxt-event-desc">
                    {FEATURED_EVENT.description}
                  </p>
                )}
              </div>

              <div className="nxt-event-foot">
                <span className="nxt-event-cta">
                  Reserve <span aria-hidden="true">→</span>
                </span>
                <span
                  className="nxt-event-alt"
                  onClick={(e) => {
                    e.stopPropagation();
                    openGuestList({
                      prefillDate: FEATURED_EVENT.date,
                      eventName: FEATURED_EVENT.title,
                    });
                  }}
                >
                  Guest list
                </span>
              </div>
            </button>
          ) : (
            <div className="nxt-event nxt-event--empty">
              <div className="nxt-event-head">
                <span className="nxt-event-num">01 / NIGHTLIFE</span>
                <span className="nxt-event-date">Coming soon</span>
              </div>
              <div className="nxt-event-body">
                <h3 className="nxt-event-title">Next event TBA</h3>
                <p className="nxt-event-desc">
                  Check back soon or join the guest list to be the first to
                  know.
                </p>
              </div>
              <div className="nxt-event-foot">
                <button
                  className="nxt-event-cta nxt-event-cta--button"
                  onClick={() => openGuestList()}
                >
                  Join the list <span aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          )}

          {/* 02 — Shop */}
          <a
            className="nxt-venture"
            href="https://shop.nxtlvlnights.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="nxt-venture-num">02</span>
            <div className="nxt-venture-body">
              <h3 className="nxt-venture-title">Shop</h3>
              <p className="nxt-venture-desc">
                Streetwear and limited drops. Wear the brand.
              </p>
            </div>
            <span className="nxt-venture-cta">
              Browse <span aria-hidden="true">↗</span>
            </span>
          </a>

          {/* 03 — About */}
          <button className="nxt-venture" onClick={onEnter}>
            <span className="nxt-venture-num">03</span>
            <div className="nxt-venture-body">
              <h3 className="nxt-venture-title">The Brand</h3>
              <p className="nxt-venture-desc">
                Who we are, what we&rsquo;re building, and what&rsquo;s next.
              </p>
            </div>
            <span className="nxt-venture-cta">
              Enter <span aria-hidden="true">→</span>
            </span>
          </button>

          {/* 04 — The Founder */}
          <a
            className="nxt-venture nxt-venture--founder"
            href="https://dannymunoz.net"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="nxt-venture-num">04</span>
            <div className="nxt-venture-body">
              <h3 className="nxt-venture-title">The Founder</h3>
              <p className="nxt-venture-desc">
                Danny Munoz. The operator behind the network and Valen Group.
              </p>
            </div>
            <span className="nxt-venture-cta">
              Visit <span aria-hidden="true">↗</span>
            </span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="nxt-footer">
        <div className="nxt-footer-left">
          <span className="nxt-footer-mark">© NXTLVL</span>
          <span className="nxt-footer-sep">·</span>
          <a
            href="https://dannymunoz.net"
            target="_blank"
            rel="noopener noreferrer"
            className="nxt-footer-link"
          >
            Founded by Danny Munoz · Orlando, FL
          </a>
          <span className="nxt-footer-sep">·</span>
          <a
            href="https://valengroup.co"
            target="_blank"
            rel="noopener noreferrer"
            className="nxt-footer-link"
          >
            A Valen Group company
          </a>
        </div>
        <div className="nxt-socials">
          <a
            href="https://instagram.com/nxtlvlnights"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <SiInstagram />
          </a>
          <a
            href="https://wa.me/16892679495"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
          >
            <SiWhatsapp />
          </a>
          <a href="tel:+16892679495" aria-label="Phone">
            <FaPhone />
          </a>
          <a href="mailto:vip@nxtlvlnights.com" aria-label="Email">
            <FaEnvelope />
          </a>
        </div>
      </footer>

      {modalState && (
        <BookingModal
          type={modalState.type}
          prefillDate={modalState.prefillDate}
          eventName={modalState.eventName}
          onClose={() => setModalState(null)}
        />
      )}
    </div>
  );
}

Landing.propTypes = {
  onEnter: PropTypes.func.isRequired,
};

export default Landing;
